import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { userDisplay, type AppUser } from "../../../services/userService";
import {
    austauschFehlertext,
    vorgangAustauschService,
    type AustauschAenderung,
    type AustauschDto,
    type AustauschTeilnehmer,
} from "../../../services/vorgangAustauschService";
import { RdPopover } from "../../redesign/table/RdPopover";
import { kuerzelVon } from "../shared/people";
import { UserKreis } from "../v3/VerlaufEintraege";
import { IcoCheck, IcoPlus } from "../v3/vg3Icons";
import {
    AUSTAUSCH_TEILNEHMER_MAX,
    austauschTitel,
    austauschZeitText,
    erledigteAustausche,
    externerName,
    offenerAustausch,
    sucheKollegen,
    teilnehmerAnzeige,
    teilnehmerAnzeigen,
    teilnehmerGleich,
    teilnehmerHinzu,
    teilnehmerKey,
    teilnehmerKurz,
    type TeilnehmerAnzeige,
} from "./austauschAnzeige";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { BottomSheet } from "../../calendar/BottomSheet";
import { useSheetKlasse } from "./mobil/sheetKlasse";

/**
 * Das A für „Austausch" (T-302) — es steht dort, wo bis zum 29.10.2026 das ⊕
 * stand: im Vorgangskopf (Vorgang ohne To-dos) und auf jeder To-do-Karte.
 *
 * Ticket (Sascha Hahn, 13.10.2026): „Es fehlt eine Art ‚Rücksprachebutton' in
 * den Vorgängen, z. B. R/DC." Seit PO 01.11.2026 geht es NUR darum, zu
 * kennzeichnen, DASS man einen Austausch hat und MIT WEM: mehrere Kolleginnen
 * und Kollegen, dazu frei eingetippte Namen (auch Externe). Datum, Uhrzeit und
 * Notiz sind weggefallen.
 *
 *     (A)  grau     — kein Austausch offen; Klick: eintragen
 *     (A)  Marke    — offen; Tooltip „Austausch mit Dora Courtmann (DC), …";
 *                     Klick: Personen ändern, „Erledigt"
 *
 * Am Knopf bleibt das „A": Er steht in der engen runden Knopfreihe der
 * To-do-Karte. Das ausgeschriebene „Austausch" trägt die Vorgangsliste, wo
 * der Zustand gelesen wird (`AustauschChip`).
 *
 * Ein Austausch gehört zu GENAU EINEM Strang: zum Vorgang selbst
 * (`todoId` null) oder zu diesem To-do. Der Knopf bekommt die Liste des
 * ganzen Vorgangs und sucht sich seinen Strang selbst.
 *
 * Wer den Vorgang nicht bearbeiten darf, sieht den Stand, ändert aber nichts.
 * Das Popover passt sich dem Platz an (`anPlatzAnpassen`): in einem kleinen
 * Fenster klappt es dorthin, wo mehr Raum ist, und rollt sonst in sich.
 */
type Props = {
    vorgangId: number;
    /** Der Strang: das To-do, oder `null` = der Vorgang selbst. */
    todoId: number | null;
    /** Alle Austausche des Vorgangs (offen und erledigt) — der Knopf wählt seinen Strang. */
    austausch: readonly AustauschDto[];
    users: AppUser[];
    canEdit: boolean;
    /** Nach jedem Schreiben — die Seite lädt die Liste neu. */
    onGeaendert: () => void | Promise<void>;
    /**
     * HANDY (Plan „Vorgangsdetail mobil", PR 7): dasselbe Formular in einem
     * Sheet statt im Popover — ein Popover neben einem 40-px-Knopf passt dort
     * nicht. Inhalt und Regeln sind dieselben (`AustauschInhalt`).
     */
    alsSheet?: boolean;
};

/**
 * Was im Popover bzw. Sheet steht — Formular für Bearbeitende, sonst der
 * Stand. Auch das Plus auf dem Handy zeigt genau das (Kachel „Austausch").
 */
export const AustauschInhalt: React.FC<{
    vorgangId: number;
    todoId: number | null;
    austausch: readonly AustauschDto[];
    users: AppUser[];
    canEdit: boolean;
    onGeaendert: () => void | Promise<void>;
    onFertig: () => void;
}> = ({ vorgangId, todoId, austausch, users, canEdit, onGeaendert, onFertig }) => {
    const offen = offenerAustausch(austausch, todoId);
    const erledigte = erledigteAustausche(austausch, todoId).slice(0, ERLEDIGTE_MAX);
    return canEdit ? (
        <AustauschFormular
            vorgangId={vorgangId}
            todoId={todoId}
            offen={offen}
            erledigte={erledigte}
            users={users}
            onFertig={onFertig}
            onGeaendert={onGeaendert}
        />
    ) : (
        <AustauschAnsicht offen={offen} erledigte={erledigte} users={users} />
    );
};

/** Wie viele erledigte das Popover unten zeigt — es ist ein Rückblick, kein Archiv. */
const ERLEDIGTE_MAX = 3;

export const AustauschKnopfV4: React.FC<Props> = ({
    vorgangId,
    todoId,
    austausch,
    users,
    canEdit,
    onGeaendert,
    alsSheet = false,
}) => {
    const offen = offenerAustausch(austausch, todoId);
    const erledigte = erledigteAustausche(austausch, todoId).slice(0, ERLEDIGTE_MAX);
    const titel = offen ? austauschTitel(offen, users) : canEdit ? "Austausch eintragen" : "Kein Austausch offen";
    const [sheet, setSheet] = useState(false);
    useSheetKlasse(sheet);

    if (alsSheet) {
        return (
            <>
                <button
                    type="button"
                    className={`vg3-ibtn vg4-austausch${offen ? " aktiv" : ""}`}
                    title={titel}
                    aria-label={titel}
                    aria-haspopup="dialog"
                    aria-expanded={sheet}
                    onClick={() => setSheet(true)}
                >
                    A
                </button>
                <BottomSheet open={sheet} title="Austausch" onClose={() => setSheet(false)}>
                    <AustauschInhalt
                        vorgangId={vorgangId}
                        todoId={todoId}
                        austausch={austausch}
                        users={users}
                        canEdit={canEdit}
                        onGeaendert={onGeaendert}
                        onFertig={() => setSheet(false)}
                    />
                </BottomSheet>
            </>
        );
    }

    return (
        <RdPopover
            width={340}
            anPlatzAnpassen
            dialogLabel="Austausch"
            align="end"
            trigger={({ open, toggle, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    className={`vg3-ibtn vg4-austausch${offen ? " aktiv" : ""}`}
                    title={titel}
                    aria-label={titel}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    onClick={toggle}
                >
                    A
                </button>
            )}
        >
            {(close) =>
                canEdit ? (
                    <AustauschFormular
                        vorgangId={vorgangId}
                        todoId={todoId}
                        offen={offen}
                        erledigte={erledigte}
                        users={users}
                        onFertig={close}
                        onGeaendert={onGeaendert}
                    />
                ) : (
                    <AustauschAnsicht offen={offen} erledigte={erledigte} users={users} />
                )
            }
        </RdPopover>
    );
};

/** Ein Name im Popover — Kollegin mit Kreis und Kürzel, Externe mit „extern". */
const PersonInhalt: React.FC<{ t: TeilnehmerAnzeige }> = ({ t }) => (
    <>
        {t.extern ? (
            <span className="vg4-austausch-extern-kreis" aria-hidden>
                {t.name.trim().charAt(0).toUpperCase() || "?"}
            </span>
        ) : (
            <UserKreis name={t.name} initials={t.kurz} userId={t.userId} size={18} />
        )}
        <span className="vg4-austausch-chip-name">{t.name}</span>
        {t.extern ? (
            <span className="vg4-austausch-extern">extern</span>
        ) : (
            <span className="vg4-austausch-kuerzel">{t.kurz}</span>
        )}
    </>
);

/** Lesende: der Stand, ohne ein einziges Eingabefeld. */
const AustauschAnsicht: React.FC<{ offen: AustauschDto | null; erledigte: AustauschDto[]; users: AppUser[] }> = ({
    offen,
    erledigte,
    users,
}) => {
    const personen = offen ? teilnehmerAnzeigen(offen.teilnehmer, users) : [];
    return (
        // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der Seite;
        // ohne eigene Hülle griffen weder Regeln noch Tokens.
        <div className="kvp-rd vg4-austausch-pop">
            <div className="vg4-austausch-kopf">Austausch</div>
            {offen && personen.length ? (
                <div className="vg4-austausch-feld">
                    <span className="vg4-austausch-label">Mit</span>
                    <ul className="vg4-austausch-chips lesend" aria-label="Austausch mit">
                        {personen.map((t) => (
                            <li key={t.key} className="vg4-austausch-chip">
                                <PersonInhalt t={t} />
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <p className="vg3-leer vg4-austausch-leer">Kein Austausch offen.</p>
            )}
            <ErledigteListe erledigte={erledigte} users={users} />
        </div>
    );
};

const ErledigteListe: React.FC<{ erledigte: AustauschDto[]; users: AppUser[] }> = ({ erledigte, users }) =>
    erledigte.length ? (
        <div className="vg4-austausch-erledigte">
            <div className="vg4-austausch-label">Zuletzt erledigt</div>
            <ul>
                {erledigte.map((a) => {
                    const personen = teilnehmerAnzeigen(a.teilnehmer, users);
                    return (
                        <li key={a.id}>
                            <span className="vg4-austausch-erledigt-kopf" title={austauschTitel(a, users)}>
                                {austauschZeitText(a.erledigtAm)} · {teilnehmerKurz(personen)}
                            </span>
                            {/* Notizen gibt es nur noch an alten Austauschen (bis 01.11.2026). */}
                            {a.notiz && <span className="vg4-austausch-erledigt-notiz">{a.notiz}</span>}
                        </li>
                    );
                })}
            </ul>
        </div>
    ) : null;

/** Eine Kollegin, deren Kürzel/Benutzername genau dem Getippten entspricht. */
function exakterKollege(users: readonly AppUser[], text: string): AppUser | null {
    const q = text.trim().toLowerCase();
    if (!q) return null;
    return (
        users.find((u) => kuerzelVon(u, userDisplay(u)).toLowerCase() === q || (u.username ?? "").toLowerCase() === q) ??
        null
    );
}

const AustauschFormular: React.FC<{
    vorgangId: number;
    todoId: number | null;
    offen: AustauschDto | null;
    erledigte: AustauschDto[];
    users: AppUser[];
    onFertig: () => void;
    onGeaendert: () => void | Promise<void>;
}> = ({ vorgangId, todoId, offen, erledigte, users, onFertig, onGeaendert }) => {
    const [teilnehmer, setTeilnehmer] = useState<AustauschTeilnehmer[]>(() => [...(offen?.teilnehmer ?? [])]);
    const [suche, setSuche] = useState("");
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const eingabe = useRef<HTMLInputElement>(null);

    const gewaehlteIds = useMemo(
        () => new Set(teilnehmer.flatMap((t) => ("userId" in t ? [t.userId] : []))),
        [teilnehmer],
    );
    const voll = teilnehmer.length >= AUSTAUSCH_TEILNEHMER_MAX;
    const treffer = voll ? [] : sucheKollegen(users, suche, 6, gewaehlteIds);
    const extern = voll ? null : externerName(suche);
    // „Als extern" nur anbieten, wenn der Text nicht schon ein Kürzel ist.
    const externAngebot = extern && !exakterKollege(users, suche) ? extern : null;
    const anzeigen = teilnehmerAnzeigen(teilnehmer, users);

    const hinzu = (t: AustauschTeilnehmer) => {
        setTeilnehmer((l) => teilnehmerHinzu(l, t));
        setSuche("");
        setFehler(null);
        eingabe.current?.focus();
    };
    const weg = (key: string) => {
        setTeilnehmer((l) => l.filter((t) => teilnehmerKey(t) !== key));
        eingabe.current?.focus();
    };

    /**
     * Was im Eingabefeld steht, als Teilnehmer übernehmen. `ersterTreffer`:
     * Enter nimmt — wie bisher — den ersten Treffer („wer DC tippt, meint DC");
     * ein Komma nimmt nur ein genau passendes Kürzel, sonst den Text als
     * externen Namen („Herr Müller, Frau Kraus").
     */
    const uebernimmEingabe = (ersterTreffer: boolean): AustauschTeilnehmer | null => {
        if (!suche.trim() || voll) return null;
        const kollege = ersterTreffer ? treffer[0] ?? null : exakterKollege(users, suche);
        if (kollege && !gewaehlteIds.has(kollege.id)) return { userId: kollege.id };
        if (kollege) return null;
        const name = externerName(suche);
        return name ? { name } : null;
    };

    const liste = (): AustauschTeilnehmer[] => {
        // Noch Getipptes zählt mit — wer „Frau Kraus" schreibt und gleich auf
        // Speichern drückt, meint sie auch.
        const offenEingabe = uebernimmEingabe(true);
        return offenEingabe ? teilnehmerHinzu(teilnehmer, offenEingabe) : teilnehmer;
    };
    const gueltig = teilnehmer.length > 0 || !!suche.trim();
    const geaendert = !offen || !teilnehmerGleich(teilnehmer, offen.teilnehmer) || !!suche.trim();

    const speichern = async (erledigen: boolean) => {
        if (busy) return;
        const neu = liste();
        if (!neu.length) {
            setFehler("Bitte mindestens eine Person wählen oder einen Namen eintragen.");
            return;
        }
        setBusy(true);
        setFehler(null);
        try {
            if (offen) {
                // Nur, was sich geändert hat — der Server meldet sonst eine
                // Änderung, die niemand gemacht hat.
                const aenderung: AustauschAenderung = {};
                if (!teilnehmerGleich(neu, offen.teilnehmer)) aenderung.teilnehmer = neu;
                if (erledigen) aenderung.erledigt = true;
                if (Object.keys(aenderung).length) await vorgangAustauschService.aendern(offen.id, aenderung);
            } else {
                await vorgangAustauschService.anlegen(vorgangId, { todoId, teilnehmer: neu });
            }
        } catch (err) {
            console.error("[AustauschKnopfV4] Speichern fehlgeschlagen", { vorgangId, todoId, err });
            setFehler(austauschFehlertext(err));
            setBusy(false);
            return;
        }
        onFertig();
        await onGeaendert();
    };

    return (
        <div className="kvp-rd vg4-austausch-pop">
            <div className="vg4-austausch-kopf">Austausch</div>

            <div className="vg4-austausch-feld">
                <span className="vg4-austausch-label" id={`vg4-austausch-mit-${vorgangId}-${todoId ?? "v"}`}>
                    Mit wem?
                </span>
                <div
                    className="vg4-austausch-chips"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) eingabe.current?.focus();
                    }}
                >
                    {anzeigen.map((t) => (
                        <span key={t.key} className={`vg4-austausch-chip${t.extern ? " extern" : ""}`}>
                            <PersonInhalt t={t} />
                            <button
                                type="button"
                                className="vg4-austausch-chip-x"
                                aria-label={`${t.name} entfernen`}
                                title={`${t.name} entfernen`}
                                onClick={() => weg(t.key)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    {!voll && (
                        <input
                            ref={eingabe}
                            autoFocus
                            className="vg4-austausch-chip-in"
                            value={suche}
                            placeholder={teilnehmer.length ? "Weitere Person…" : "Name, Kürzel oder extern…"}
                            aria-label="Person suchen oder Namen eintragen"
                            aria-describedby={`vg4-austausch-mit-${vorgangId}-${todoId ?? "v"}`}
                            onChange={(e) => {
                                const wert = e.target.value;
                                // Komma/Semikolon am Ende macht den Chip — auch beim Einfügen.
                                if (/[,;]\s*$/.test(wert) && wert.replace(/[,;\s]/g, "")) {
                                    const t = (() => {
                                        const text = wert.replace(/[,;]\s*$/, "");
                                        const k = exakterKollege(users, text);
                                        if (k) return gewaehlteIds.has(k.id) ? null : { userId: k.id };
                                        const name = externerName(text);
                                        return name ? { name } : null;
                                    })();
                                    if (t) hinzu(t);
                                    else setSuche("");
                                    return;
                                }
                                setSuche(wert);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    const t = uebernimmEingabe(true);
                                    if (t) hinzu(t);
                                    else if (!suche.trim() && teilnehmer.length) void speichern(false);
                                } else if (e.key === "Backspace" && !suche && teilnehmer.length) {
                                    // Wie in jedem Empfängerfeld: Rücktaste nimmt den letzten Chip.
                                    setTeilnehmer((l) => l.slice(0, -1));
                                }
                            }}
                        />
                    )}
                </div>
                {!voll && (treffer.length > 0 || externAngebot) && (
                    <div className="vg3-personen-treffer vg4-austausch-trefferliste" role="listbox" aria-label="Vorschläge">
                        {treffer.map((u) => {
                            const t = teilnehmerAnzeige({ userId: u.id }, users);
                            return (
                                <button
                                    key={u.id}
                                    type="button"
                                    role="option"
                                    aria-selected={false}
                                    className="vg3-tbtn vg4-austausch-treffer"
                                    onClick={() => hinzu({ userId: u.id })}
                                >
                                    <span className="vg4-austausch-treffer-name">{t.name}</span>
                                    <span className="vg4-austausch-kuerzel">{t.kurz}</span>
                                </button>
                            );
                        })}
                        {externAngebot && (
                            <button
                                type="button"
                                role="option"
                                aria-selected={false}
                                className="vg3-tbtn vg4-austausch-treffer vg4-austausch-treffer-extern"
                                onClick={() => hinzu({ name: externAngebot })}
                            >
                                <span className="vg4-austausch-treffer-name">
                                    <IcoPlus size={11} /> „{externAngebot}“ als externe Person
                                </span>
                            </button>
                        )}
                    </div>
                )}
                {voll && (
                    <span className="vg3-leer">Mehr als {AUSTAUSCH_TEILNEHMER_MAX} Personen gehen nicht.</span>
                )}
            </div>

            {fehler && (
                <div className="vg3-saveerr vg4-austausch-fehler" role="alert">
                    {fehler}
                </div>
            )}

            <div className="vg4-austausch-akt">
                <button
                    type="button"
                    className="vg3-btn primary"
                    disabled={!gueltig || !geaendert || busy}
                    onClick={() => void speichern(false)}
                >
                    Speichern
                </button>
                {offen && (
                    <button
                        type="button"
                        className="vg3-btn vg4-austausch-erledigen"
                        disabled={!gueltig || busy}
                        title="Austausch als erledigt markieren — danach ist das A wieder grau"
                        onClick={() => void speichern(true)}
                    >
                        <IcoCheck size={12} /> Erledigt
                    </button>
                )}
                <button type="button" className="vg3-btn" onClick={onFertig}>
                    Abbrechen
                </button>
            </div>

            <ErledigteListe erledigte={erledigte} users={users} />
        </div>
    );
};
